import React, { FC, useContext, useEffect, useRef, useState } from 'react'
import { Box, Flex } from '@/components/atoms/Grid'
import styled, { css, useTheme } from 'styled-components'
import { Tiny, Tiny2 } from '@/components/atoms/typography'
import {
  Desktop,
  MediaContextProvider,
  TabletOrMobile
} from '@/components/atoms/media'
import Link from '@/components/atoms/link'
import { Routes } from '@/core/routes'
import { validFor } from '@/theme/validFor'
import { useWishlistDispatch } from '@/providers/wishlistDispatchProvider'
import PriceList from '@/components/molecules/priceList'
import { BridgeProduct } from 'types/bridgeProduct'
import RemoveButton from '@/components/molecules/removeButton'
import useTranslation from 'next-translate/useTranslation'
import { CurrencyContext } from '@/providers/currencyProvider'
import ResponsiveImageNew from '@/components/atoms/responsiveImageNew'
import { Config } from '@/core/config'
import {
  Product,
  ProductLabelType,
  ProductPrices,
  ProductRateData,
  ProductSize
} from '@/network/graphql.g'
import ProductLabels, {
  ProductLabelStyle
} from '@/components/organisms/productLabels'
import {
  DefaultProductCardColors,
  PlaceholderText,
  ProductCardColors
} from '@/components/atoms/productCardPlaceholder'
import { InView } from 'react-intersection-observer'
import Confirm from '@/components/organisms/confirm'
import { ProductCardMode } from '@/components/organisms/productCard/productCardMode'
import { useRouter } from 'next/router'
import { useWishlist } from '@/providers/wishlistProvider'
import { useMhub } from '@/providers/mhubProvider'
import { applyImagePolicy, ImageMode, ImageSizes } from '@/utils/imageExtension'
import useDevice from 'hooks/useDevice'
import { ProductDetailNotAvailableTitle } from '@/components/organisms/productDetailSideMenu/productDetailNotAvailableSideMenu'
import RemoveProductButtons from '@/components/molecules/iconButtons/removeProductButtons'
import { useAsset } from '@/providers/assetsProvider'
import { calculateDiscount } from '@/core/toStringHelper'
import dynamic from 'next/dynamic'
import ResponsiveImage from '@/components/atoms/responsiveImage'
import { ProductCardHoverMenuRedesign } from '@/components/organisms/productCard/productCardHoverMenuRedesign'
import { FilledHeartIconListingRedesignDefault } from '@/components/atoms/icons/FilledHearIconListingRedesignDefault'
import { createUrl } from '@/utils/urlHelper'
import ProductCardSizes, {
  useShowListingSizes
} from '@/components/organisms/productCard/productCardSizes'

const VerticalOffset = '500px'
const HorizontalOffset = '300px'

enum ExpandMode {
  expanded,
  collapsed
}

export type ProductCardActionType = (
  productId: string,
  sizeId: string,
  product?: Product
) => void

type ProductCardProps = {
  product: BridgeProduct
  onAddToCart: ProductCardActionType
  mode: ProductCardMode
  expandMode?: ExpandMode
  removeProduct?: (product: Product) => void
  position: number
  virtualScrolling?: boolean
  page?: number
  disableFetch?: () => void
  list: string
  colors?: ProductCardColors
  indentText?: boolean
  isAvailable?: boolean
  isShared?: boolean
  mainCategoryId?: string
  useFitIn?: boolean
  setWishlistItemRemoved?: React.Dispatch<
    React.SetStateAction<{ [product: string]: boolean }>
  >
}

const RatingInfoIconRedesign = dynamic(
  () => import('@/components/molecules/ratingInfoIconRedesign'),
  { ssr: false }
)

const ProductCard: FC<ProductCardProps> = ({
  product,
  position,
  virtualScrolling = true,
  mode,
  list,
  colors: initialColors,
  indentText = false,
  isAvailable,
  isShared,
  setWishlistItemRemoved,
  mainCategoryId,
  useFitIn = false,
  ...rest
}) => {
  const colors: ProductCardColors = {
    ...DefaultProductCardColors,
    ...(initialColors ?? {})
  }

  // const { pushImpression } = useMhub()
  // const { observe } = useInView({
  //   unobserveOnEnter: true,
  //   threshold: [0.5],
  //   onEnter: () => {
  //     pushImpression(product, position, list)
  //   }
  // })

  const anchorRef = useRef<HTMLLinkElement>()

  useEffect(() => {
    // remove id after render
    const handler = setTimeout(() => {
      if (anchorRef.current) {
        anchorRef.current.removeAttribute('id')
      }
    }, 5000)
    return () => clearTimeout(handler)
  }, [])

  return (
    <InView delay={200} rootMargin={`${VerticalOffset} ${HorizontalOffset}`}>
      {({ inView, ref }) => {
        const isVisible = inView || !virtualScrolling
        return (
          <ProductCardWrapper ref={ref}>
            <Anchor ref={anchorRef} id={`product-${product?.id}`} />
            {/*<div ref={observe}>*/}
            <Base
              productBrand={product?.productBrand}
              prices={product?.prices}
              isVisible={isVisible}
              mode={mode}
              colors={colors}
              indentText={indentText}
              productRateData={product?.productRateData}
              sizes={product?.sizes}
              productName={product?.name}
            >
              {isVisible ? (
                <ContentWrapper>
                  <Item
                    product={product}
                    position={position}
                    mode={mode}
                    list={list}
                    colors={colors}
                    isAvailable={isAvailable}
                    isShared={isShared}
                    setWishlistItemRemoved={setWishlistItemRemoved}
                    mainCategoryId={mainCategoryId}
                    useFitIn={useFitIn}
                    {...rest}
                  />
                </ContentWrapper>
              ) : (
                <Placeholder colors={colors} />
              )}
            </Base>
            {/*</div>*/}
          </ProductCardWrapper>
        )
      }}
    </InView>
  )
}

type BaseProps = {
  prices: ProductPrices
  isVisible: boolean
  mode: ProductCardMode
  productBrand: string
  colors: ProductCardColors
  indentText: boolean
  productRateData: ProductRateData
  sizes?: ProductSize[]
  productName: string
}

const Base: FC<BaseProps> = ({
  prices,
  isVisible,
  mode,
  children,
  productBrand,
  colors,
  indentText,
  productRateData,
  sizes,
  productName
}) => {
  const currency = useContext(CurrencyContext)
  const theme = useTheme()
  const { showReviews } = useAsset()
  const hasStock = sizes.some((item) => item.isStock === true)
  const hasAllSizesStock = sizes.every((item) => item.isStock === true)
  const showSizes = useShowListingSizes(mode)

  return (
    <PlaceholderFlexWrapper
      isVisible={isVisible}
      colors={colors}
      data-testid={
        mode === ProductCardMode.recommended
          ? 'recomEntryWrapper'
          : 'clpEntryWrapper'
      }
    >
      <StyledNamePriceWrapper
        isVisible={isVisible}
        colors={colors}
        px={indentText ? '8px' : 0}
      >
        {showSizes && (
          <MediaContextProvider>
            <TabletOrMobile>
              <ProductCardSizes sizes={isVisible ? sizes : null} />
            </TabletOrMobile>
          </MediaContextProvider>
        )}
        {productRateData?.rateTotal > 0 &&
        productRateData?.canShow &&
        showReviews ? (
          <Box mb="1px">
            <RatingInfoIconRedesign
              productRateData={productRateData}
              size={18}
              marginTop={8}
              fillColor={theme.colors.sizeTableDescription}
            />
          </Box>
        ) : null}
        <Box mt={showReviews ? '8px' : '16px'}>
          <StyledProductBrand margin={0} data-testid="productName">
            {productBrand.toUpperCase()}
          </StyledProductBrand>
        </Box>
        {productName && (
          <Box mt="4px">
            <StyledProductName margin={0} data-testid="productName">
              {productName}
            </StyledProductName>
          </Box>
        )}
        <StyledPriceListRedesignReverse
          prices={prices}
          currency={currency}
          data-testid="priceList"
          hasStock={hasStock}
          hasAllSizesStock={hasAllSizesStock}
        />
        {children}
      </StyledNamePriceWrapper>
    </PlaceholderFlexWrapper>
  )
}

const Item: FC<ProductCardProps> = ({
  product,
  mode,
  expandMode,
  onAddToCart,
  removeProduct,
  page,
  disableFetch,
  list,
  colors: productCardColors,
  position,
  isAvailable,
  isShared,
  setWishlistItemRemoved,
  mainCategoryId,
  useFitIn = false
}) => {
  const { removeFromWishlist } = useWishlistDispatch()
  const { getImpressionAttributeData, pushShowProductEvent } = useMhub()
  const { t } = useTranslation('common')
  const [hover, setHover] = useState<boolean>(false)
  const [showRemoveFromWishlistConfirm, setShowRemoveFromWishlistConfirm] =
    useState<boolean>(false)
  const router = useRouter()

  const content = (
    <ItemContent
      product={product}
      onAddToCart={onAddToCart}
      mode={mode}
      position={position}
      list={list}
      colors={productCardColors}
      hover={hover}
      expandMode={expandMode}
      setShowRemoveFromWishlistConfirm={setShowRemoveFromWishlistConfirm}
      isAvailable={isAvailable}
      isShared={isShared}
      mainCategoryId={mainCategoryId}
      useFitIn={useFitIn}
    />
  )

  const wrapperProps = {
    onMouseEnter: () => setHover(true),
    onMouseLeave: () => setHover(false),
    'data-product-impression': getImpressionAttributeData(product, list)
  }

  return (
    <>
      {mode === ProductCardMode.wishlist && showRemoveFromWishlistConfirm && (
        <Confirm
          title={t('Wishlist.textWantRemoveProduct')}
          onDidTapClose={() => setShowRemoveFromWishlistConfirm(false)}
          onSubmit={(result) => {
            if (result) {
              removeFromWishlist(product)
              removeProduct && removeProduct(product)
            }
            setShowRemoveFromWishlistConfirm(false)
            setWishlistItemRemoved((prevData) => ({
              ...prevData,
              [product.id]: false
            }))
          }}
        />
      )}
      {mode !== ProductCardMode.gifts ? (
        <StyledLink
          href={`${Routes.product}/${product?.id}`}
          onClick={(e: React.MouseEvent<HTMLLinkElement>) => {
            const isCtrlPressed = e.ctrlKey
            setTimeout(() => {
              pushShowProductEvent(product, list)
              if (
                !isCtrlPressed &&
                [
                  ProductCardMode.productList,
                  ProductCardMode.wishlist
                ].includes(mode)
              ) {
                disableFetch && disableFetch()
                const url = createUrl(router.asPath)
                url.searchParams.delete('page')
                url.searchParams.delete('slug')
                url.searchParams.delete('a')
                url.searchParams.delete('na')
                if (page) {
                  url.searchParams.append('page', page?.toString())
                }
                url.hash = ''
                url.searchParams.append('a', `product-${product?.id}`)
                try {
                  localStorage.setItem('intProductId', product?.id)
                } catch (err) {
                  // eslint-disable-next-line no-console
                  console.error(err)
                }
                window.history.replaceState(
                  {
                    ...window.history.state,
                    ...{ as: `${url.pathname}${url.search}${url.hash}` }
                  },
                  null,
                  `${url.pathname}${url.search}${url.hash}`
                )
              }
            }, 0)
          }}
          data-testid={
            mode === ProductCardMode.recommended ? 'recomEntryAnchor' : null
          }
          {...wrapperProps}
        >
          {content}
        </StyledLink>
      ) : (
        <Box
          onClick={() => onAddToCart(product?.id, null, product)}
          {...wrapperProps}
        >
          {content}
        </Box>
      )}
    </>
  )
}

const ItemContent: FC<
  ProductCardProps & {
    hover: boolean
    setShowRemoveFromWishlistConfirm: React.Dispatch<
      React.SetStateAction<boolean>
    >
  }
> = ({
  product,
  mode,
  expandMode,
  colors: productCardColors,
  hover,
  setShowRemoveFromWishlistConfirm,
  isAvailable = true,
  isShared,
  mainCategoryId
}) => {
  const { useSOIPricing } = useAsset()
  const { addToWishlist, removeFromWishlist } = useWishlistDispatch()
  const { isInWishlist } = useWishlist()
  const placeholderRef = useRef<HTMLDivElement>()
  const imageWrapperRef = useRef<HTMLDivElement>()
  const { isDesktop } = useDevice()

  const onLoadingComplete = () => {
    if (placeholderRef.current) {
      placeholderRef.current.remove()
    }
    if (imageWrapperRef.current) {
      imageWrapperRef.current.style.backgroundColor = 'transparent'
    }
  }

  const [isHydrated, setIsHydrated] = useState<boolean>(false)
  useEffect(() => {
    if (product?.preload) {
      setIsHydrated(true)
    }
  }, [])
  const useDesktopImage = isDesktop && (isHydrated || !product?.preload)

  const image = applyImagePolicy(product?.mainImage?.url, {
    size: useDesktopImage ? ImageSizes['410x615'] : ImageSizes['330x495'],
    mode: ImageMode.Local
  })

  const isNotAvailableWishlistProductCard =
    !isAvailable && (mode === ProductCardMode.wishlist || isShared)

  const hasPromoPrice = (product?.prices?.promoCodePrice || 0) > 0

  const discountBestPrice = hasPromoPrice
    ? calculateDiscount(
        product?.prices?.promoCodePrice,
        product?.prices?.minLastMonthPrice
      )
    : calculateDiscount(
        product?.prices?.finalPrice,
        product?.prices?.minLastMonthPrice
      )

  const shouldRemoveDiscountLabel =
    discountBestPrice === 0 ||
    product?.prices?.minLastMonthPrice < product?.prices?.finalPrice

  const updatedLabels = shouldRemoveDiscountLabel
    ? product?.labels.filter((item) => item.type !== ProductLabelType.Discount)
    : product?.labels.map((item) =>
        item.type === ProductLabelType.Discount
          ? { ...item, title: `${discountBestPrice}%` }
          : item
      )

  const useThumbor = false

  return (
    <FlexWrapper data-testid="productCard">
      <ImageWrapper colors={productCardColors} ref={imageWrapperRef}>
        <div className="productCardImage">
          {!useThumbor ? (
            <StyledResponsiveImage
              alt={product?.name || 'Product image'}
              imageUrl={image}
              priority={product?.preload}
              unoptimized={true}
              lazyBoundary={VerticalOffset}
              onLoadingComplete={onLoadingComplete}
              {...Config.imageSizes.xl}
              style={{ opacity: isNotAvailableWishlistProductCard ? 0.5 : 1 }}
            />
          ) : (
            <StyledResponsiveImageNew
              alt={product?.name || 'Product image'}
              imageUrl={image}
              {...Config.imageSizes.xl}
              style={{ opacity: isNotAvailableWishlistProductCard ? 0.5 : 1 }}
            />
          )}

          {hover && !isNotAvailableWishlistProductCard && (
            <MediaContextProvider>
              <Desktop>
                <StyledProductCardHoverMenuRedesign
                  pb={expandMode != null ? 45 : 0}
                  id={product?.id}
                  sizes={product?.sizes}
                  mode={mode}
                  mainCategoryId={mainCategoryId}
                />
              </Desktop>
            </MediaContextProvider>
          )}
          {mode === ProductCardMode.wishlist ? (
            <StyledRemoveButton
              data-testid="removeButton"
              onClick={(e: Event) => {
                e.preventDefault()
                e.stopPropagation()
                setShowRemoveFromWishlistConfirm(true)
              }}
            />
          ) : (
            <StyledFilledHeartIconListingRedesignDefault
              data-testid="wishlistButton"
              onClick={(e: Event) => {
                e.preventDefault()
                e.stopPropagation()
                isInWishlist(product)
                  ? removeFromWishlist(product)
                  : addToWishlist(product)
              }}
              isFavourited={isInWishlist(product)}
            />
          )}
          {!isDesktop ? (
            <RemoveProductButtons
              productId={product?.id}
              mainCategoryId={mainCategoryId}
            />
          ) : null}
          {isNotAvailableWishlistProductCard && (
            <ProductDetailNotAvailableTitle isModal={true} />
          )}
          <MediaContextProvider>
            <Desktop>
              {!hover && !isNotAvailableWishlistProductCard && (
                <StyledProductLabels
                  badges={product?.badges}
                  labels={useSOIPricing ? updatedLabels : product?.labels}
                  labelStyle={ProductLabelStyle.Listing}
                />
              )}
            </Desktop>
            <TabletOrMobile>
              {!isNotAvailableWishlistProductCard && (
                <StyledProductLabels
                  badges={product?.badges}
                  labels={useSOIPricing ? updatedLabels : product?.labels}
                  labelStyle={ProductLabelStyle.Listing}
                />
              )}
            </TabletOrMobile>
          </MediaContextProvider>
        </div>
      </ImageWrapper>
      <StyledDiv ref={placeholderRef}>
        <Placeholder position="relative" colors={productCardColors} />
      </StyledDiv>
    </FlexWrapper>
  )
}

type PlaceholderProps = {
  position?: string
  colors: ProductCardColors
} & Rest

const Placeholder: FC<PlaceholderProps> = React.forwardRef(
  ({ position = 'absolute', colors }, ref) => {
    return (
      <PricePlaceholder
        alignItems={{
          mobile: 'center',
          tablet: 'flex-start',
          desktop: 'flex-start'
        }}
        position={position}
        ref={ref}
        colors={colors}
      >
        <PlaceholderText
          mt={{ desktop: 20, tablet: 20, mobile: 11 }}
          height={10}
          width={{ desktop: '90%', tablet: '90%', mobile: '90%' }}
          colors={colors}
        />
        <PlaceholderText
          mt={13}
          height={13}
          width={{ desktop: '75%', tablet: '75%', mobile: '75%' }}
          colors={colors}
        />
      </PricePlaceholder>
    )
  }
)

export default ProductCard

const BorderRadius = css`
  border-radius: ${(props) => props.theme.borderRadius}px;
`

const StyledResponsiveImageNew = styled(ResponsiveImageNew)`
  ${BorderRadius}
`

const StyledResponsiveImage = styled(ResponsiveImage)`
  ${BorderRadius}
`

const StyledProductCardHoverMenuRedesign = styled(ProductCardHoverMenuRedesign)`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  transform: translateY(100%);
  transition: transform 0.3s ease-in-out;
  z-index: 10;
  ${BorderRadius}
`

const ExpandText = styled(Tiny2)`
  color: ${({ theme }) => theme.colors.background};
  margin: 0 10px 0 0;
  display: none;
`

const BaseFlexWrapper = css`
  overflow: hidden;
  text-align: left;
  flex-direction: column;
  justify-content: flex-start;
  position: relative;
`

const ProductCardWrapper = styled.div`
  ${BaseFlexWrapper}
`

const PlaceholderFlexWrapper = styled(Flex)`
  flex-direction: column;
  padding-top: 150%;
  ${({ isVisible, colors }) =>
    !isVisible && `background-color: ${colors.imagePlaceholder};`}
  ${BorderRadius}
`

const FlexWrapper = styled(Flex)`
  ${BaseFlexWrapper};
  height: 100%;
  &:hover {
    cursor: pointer;
    ${() =>
      css`
        ${StyledProductCardHoverMenuRedesign} {
          transform: translateY(0);
          visibility: visible;
        }
      `}
    ${ExpandText} {
      display: block;
    }
  }
`

const StyledFilledHeartIconListingRedesignDefault = styled(
  FilledHeartIconListingRedesignDefault
)<{ isFavourited?: boolean }>`
  position: absolute;
  top: 12px;
  right: 12px;
  z-index: 5;
  cursor: pointer;
  transition: fill 0.2s ease-in-out;

  path {
    fill: ${({ isFavourited }) => (isFavourited ? 'black' : 'white')};
  }

  &:hover path {
    fill: black;
  }
`

const StyledRemoveButton = styled(RemoveButton)`
  position: absolute;
  top: 12px;
  right: 12px;
  z-index: 9;
`
const OverLayImages = css`
  &::before {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    top: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.04);
    z-index: 1;
  }
`
const ImageWrapper = styled(Box)`
  ${BorderRadius};
  background-color: ${({ colors }) => colors.imagePlaceholder};
  position: relative;
  width: 100%;
  flex-shrink: 0;
  overflow: hidden;
  > * {
    &:first-child {
      width: 100%;
    }
  }

  .productCardImage {
    ${OverLayImages};
  }
`

const StyledProductLabels = styled(ProductLabels)`
  bottom: 5px;
`

const StyledProductBrand = styled(Tiny)`
  font-size: 16px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  line-height: 100%;
  color: ${({ theme }) => theme.colors.buttonColorNew};
  ${validFor.mobile`
  font-size: 14px;
  `}
`

const StyledProductName = styled(Tiny)`
  font-size: 14px;
  font-weight: 400;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  line-height: 1.2;
`

const PlaceholderBackgroundColor = css`
  background-color: ${(props) => props.colors.background};
`

const StyledPriceListRedesignReverse = styled(PriceList)`
  gap: 6px;
  margin-top: 8px;
`

const StyledNamePriceWrapper = styled(Box)`
  ${PlaceholderBackgroundColor}
  ${({ isVisible }) => !isVisible && 'position: relative;'}
`

const PricePlaceholder = styled(Flex)`
  height: 100%;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  flex-direction: column;
  ${PlaceholderBackgroundColor}
`

const ContentWrapper = styled(Box)`
  display: 'inherit';
  position: absolute;
  top: 0;
  bottom: 0;
  right: 0;
  left: 0;
`

const StyledLink = styled(Link)`
  height: 100%;
`

const Anchor = styled.span`
  position: absolute;
  top: -74px;
`

const StyledDiv = styled.div`
  height: 100%;
`
